import { useMemo } from 'react';
import * as _ from 'lodash';
import { IPatientOutcomes } from '@nkr/models/index';
import { GridWrapper } from './styles';
import { AgGridReact, AgGridColumnProps, AgGridColumnGroupProps } from 'ag-grid-react';
import {
    IHeaderParams, GridReadyEvent,
    ValueGetterParams, FirstDataRenderedEvent, ICellRendererParams, GridSizeChangedEvent, SortChangedEvent
} from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import ReactHtmlParser from 'react-html-parser';

type MyColumn = AgGridColumnProps | AgGridColumnGroupProps;

interface IProps {
    data: IPatientOutcomes[],
    onGridReady: (e: GridReadyEvent) => void
}
const PatientOutComesReportGrid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        props.onGridReady(e);
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        // Intentionally no sizeColumnsToFit / hide-columns logic.
        // This report has lots of columns; we want horizontal scrolling rather than crunched columns.
        // (AG Grid will virtualize columns efficiently.)
    };

    const reportColumns: MyColumn[] = [
        {
            colId: 'Rank',
            field: 'rank',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    R#
                </SortableHeaderComponent>,
            headerName: 'Rank',
            width: 55,
            sortable: false,
            resizable: true,
            suppressSizeToFit: true,
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
        },
        {
            colId: 'swapNumber',
            field: 'swapNumber',
            headerName: 'swapNumber',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Swap<br />#
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'swapCluster',
            field: 'swapCluster',
            headerName: 'swapCluster',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Cluster<br />#
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'swapPosition',
            field: 'swapPosition',
            headerName: 'swapPosition',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Swap<br />Pos
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'transplantDate',
            field: 'transplantDate',
            headerName: 'transplantDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Transplant<br />Date
                </SortableHeaderComponent>,
            width: 120,
        },
        {
            colId: 'unosId',
            field: 'unosId',
            headerName: 'unosId',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    UNOS<br />ID
                </SortableHeaderComponent>,
            width: 110,
        },
        {
            colId: 'isChainEndRecipient',
            field: 'isChainEndRecipient',
            headerName: 'isChainEndRecipient',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Is<br />Chain<br />End
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'isVoucherRecipient',
            field: 'isVoucherRecipient',
            headerName: 'isVoucherRecipient',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Is<br />Voucher<br />Recipient
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'isDirect',
            field: 'isDirect',
            headerName: 'isDirect',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Is<br />Direct
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'isRemoteDirect',
            field: 'isRemoteDirect',
            headerName: 'isRemoteDirect',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Is<br />Remote<br />Direct
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCenter',
            field: 'recipientCenter',
            headerName: 'recipientCenter',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Center
                </SortableHeaderComponent>,
            width: 160,
        },
        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'recipientAlias',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Alias
                </SortableHeaderComponent>,
            width: 130,
        },
        {
            colId: 'recipientType',
            field: 'recipientType',
            headerName: 'recipientType',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Type
                </SortableHeaderComponent>,
            width: 160,
        },
        {
            colId: 'recipientsIntendedDonorType',
            field: 'recipientsIntendedDonorType',
            headerName: 'recipientsIntendedDonorType',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Intended<br />Donor<br />Type
                </SortableHeaderComponent>,
            width: 160,
        },
        {
            colId: 'recipientsIntendedDonorRelationship',
            field: 'recipientsIntendedDonorRelationship',
            headerName: 'recipientsIntendedDonorRelationship',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Intended<br />Donor<br />Relation
                </SortableHeaderComponent>,
            width: 120,
        },
        {
            colId: 'recipientExcludedFromDataCompliance',
            field: 'recipientExcludedFromDataCompliance',
            headerName: 'recipientExcludedFromDataCompliance',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Excluded<br />From Data<br />Compliance
                </SortableHeaderComponent>,
            width: 100,
        },
        {
            colId: 'recipientLostToFollowup',
            field: 'recipientLostToFollowup',
            headerName: 'recipientLostToFollowup',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Lost<br />to<br />F/U
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientGender',
            field: 'recipientGender',
            headerName: 'recipientGender',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Gender
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientHeight',
            field: 'recipientHeight',
            headerName: 'recipientHeight',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Height
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientWeight',
            field: 'recipientWeight',
            headerName: 'recipientWeight',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Weight
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientEthnicity',
            field: 'recipientEthnicity',
            headerName: 'recipientEthnicity',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Ethnicity
                </SortableHeaderComponent>,
            width: 170,
        },
        {
            colId: 'recipientAbo',
            field: 'recipientAbo',
            headerName: 'recipientAbo',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />ABO
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCmvStatus',
            field: 'recipientCmvStatus',
            headerName: 'recipientCmvStatus',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />CMV
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientEbvStatus',
            field: 'recipientEbvStatus',
            headerName: 'recipientEbvStatus',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />EBV
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientAntiATiters',
            field: 'recipientAntiATiters',
            headerName: 'recipientAntiATiters',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Anti-A<br />Titers
                </SortableHeaderComponent>,
            width: 110,
        },
        {
            colId: 'recipientAntiATiterValue',
            field: 'recipientAntiATiterValue',
            headerName: 'recipientAntiATiterValue',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Anti-A<br />Titer Value
                </SortableHeaderComponent>,
            width: 110,
        },
        {
            colId: 'recipientNkrCpra',
            field: 'recipientNkrCpra',
            headerName: 'recipientNkrCpra',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />NKR<br />CPRA
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientUnosCpra',
            field: 'recipientUnosCpra',
            headerName: 'recipientUnosCpra',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />UNOS<br />CPRA
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientUnosListDate',
            field: 'recipientUnosListDate',
            headerName: 'recipientUnosListDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />UNOS<br />List Date
                </SortableHeaderComponent>,
            width: 100,
        },
        {
            colId: 'recipientIsPreemptive',
            field: 'recipientIsPreemptive',
            headerName: 'recipientIsPreemptive',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Is<br />Preemptive
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientDialysisStartDate',
            field: 'recipientDialysisStartDate',
            headerName: 'recipientDialysisStartDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Dialysis<br />Start
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientDialysisMonths',
            field: 'recipientDialysisMonths',
            headerName: 'recipientDialysisMonths',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Dialysis<br />Months
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientNkrWaitDays',
            field: 'recipientNkrWaitDays',
            headerName: 'recipientNkrWaitDays',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    NKR<br />Wait<br />Days
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientNkrWaitMonths',
            field: 'recipientNkrWaitMonths',
            headerName: 'recipientNkrWaitMonths',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    NKR<br />Wait<br />Months
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientAgeAtTransplant',
            field: 'recipientAgeAtTransplant',
            headerName: 'recipientAgeAtTransplant',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Age<br />at<br />Transplant
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'donorCenter',
            field: 'donorCenter',
            headerName: 'donorCenter',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Center
                </SortableHeaderComponent>,
            width: 100,
        },
        {
            colId: 'donorAlias',
            field: 'donorAlias',
            headerName: 'donorAlias',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Alias
                </SortableHeaderComponent>,
            width: 120,
        },
        {
            colId: 'donorType',
            field: 'donorType',
            headerName: 'donorType',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Type
                </SortableHeaderComponent>,
            width: 150,
        },
        {
            colId: 'donorGender',
            field: 'donorGender',
            headerName: 'donorGender',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Gender
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'donorHeight',
            field: 'donorHeight',
            headerName: 'donorHeight',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Height<br />(cm)
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'donorWeight',
            field: 'donorWeight',
            headerName: 'donorWeight',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Weight<br />(kg)
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'donorEthnicity',
            field: 'donorEthnicity',
            headerName: 'donorEthnicity',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Ethnicity
                </SortableHeaderComponent>,
            width: 100,
        },
        {
            colId: 'donorAbo',
            field: 'donorAbo',
            headerName: 'donorAbo',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />ABO
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'donorKidneySide',
            field: 'donorKidneySide',
            headerName: 'donorKidneySide',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Kidney<br />Side
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'donorKidneyArteries',
            field: 'donorKidneyArteries',
            headerName: 'donorKidneyArteries',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Kidney<br /># Arteries
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'donorKidneyVeins',
            field: 'donorKidneyVeins',
            headerName: 'donorKidneyVeins',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Kidney<br /># Veins
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'donorKidneyUreters',
            field: 'donorKidneyUreters',
            headerName: 'donorKidneyUreters',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Kidney<br /># Ureters
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'donorCreatinineClearance',
            field: 'donorCreatinineClearance',
            headerName: 'donorCreatinineClearance',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Creatinine<br />Clearance
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'donorSerumCreatinine',
            field: 'donorSerumCreatinine',
            headerName: 'donorSerumCreatinine',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Serum<br />Creatinine
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'donorUrineProtein',
            field: 'donorUrineProtein',
            headerName: 'donorUrineProtein',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Urine<br />Protein
                </SortableHeaderComponent>,
            width: 75,
            cellStyle: {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
            tooltipValueGetter: (p: any) => (p?.value ?? '') as string,
            cellRendererFramework: (p: ICellRendererParams) => {
                const value = (p.value ?? '') as string;
                return (
                    <span title={value} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                        {value}
                    </span>
                );
            },
        },
        {
            colId: 'donorAgeAtTransplant',
            field: 'donorAgeAtTransplant',
            headerName: 'donorAgeAtTransplant',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Age at<br />Transplant
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCenterPostalCode',
            field: 'recipientCenterPostalCode',
            headerName: 'recipientCenterPostalCode',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Center<br />Zip
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'donorCenterPostalCode',
            field: 'donorCenterPostalCode',
            headerName: 'donorCenterPostalCode',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor<br />Center<br />Zip
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'kidneyPickupDateTime',
            field: 'kidneyPickupDateTime',
            headerName: 'kidneyPickupDateTime',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Kidney<br />Pickup<br />Date/Time
                </SortableHeaderComponent>,
            width: 190,
        },
        {
            colId: 'kidneyArrivalDateTime',
            field: 'kidneyArrivalDateTime',
            headerName: 'kidneyArrivalDateTime',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Kidney<br />Arrival<br />Date/Time
                </SortableHeaderComponent>,
            width: 190,
        },
        {
            colId: 'kidneyTransitTimeMinutes',
            field: 'kidneyTransitTimeMinutes',
            headerName: 'kidneyTransitTimeMinutes',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Transit<br />Time<br />(Minutes)
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'kidneyTransitType',
            field: 'kidneyTransitType',
            headerName: 'kidneyTransitType',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Transit<br />Type
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'kidneyTransitMiles',
            field: 'kidneyTransitMiles',
            headerName: 'kidneyTransitMiles',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Transit<br />Miles
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'citHours',
            field: 'citHours',
            headerName: 'citHours',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    CIT<br />Hours
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'hlaMatchPoints',
            field: 'hlaMatchPoints',
            headerName: 'hlaMatchPoints',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    HLA<br />Match<br />Points
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'hlaMismatchesA',
            field: 'hlaMismatchesA',
            headerName: 'hlaMismatchesA',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    HLA<br />Mismatches<br />A
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'hlaMismatchesB',
            field: 'hlaMismatchesB',
            headerName: 'hlaMismatchesB',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    HLA<br />Mismatches<br />B
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'hlaMismatchesDr',
            field: 'hlaMismatchesDr',
            headerName: 'hlaMismatchesDr',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    HLA<br />Mismatches<br />DR
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'epletMmRisk',
            field: 'epletMmRisk',
            headerName: 'epletMmRisk',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Eplet<br />MM<br />Risk
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'wasDesensitized',
            field: 'wasDesensitized',
            headerName: 'wasDesensitized',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Was<br />Desensitized
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'desensitizationType',
            field: 'desensitizationType',
            headerName: 'desensitizationType',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Desensitization<br />Type
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'hadDelayedGraftFunction',
            field: 'hadDelayedGraftFunction',
            headerName: 'hadDelayedGraftFunction',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Delayed<br />Graft<br />Function
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'hadPrimaryNonFunction',
            field: 'hadPrimaryNonFunction',
            headerName: 'hadPrimaryNonFunction',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Primary<br />Non<br />Function
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'didGraftSurvive',
            field: 'didGraftSurvive',
            headerName: 'didGraftSurvive',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Did<br />Graft<br />Survive
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'graftFailureDate',
            field: 'graftFailureDate',
            headerName: 'graftFailureDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Graft<br />Failure<br />Date
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientRejectionEpisodes',
            field: 'recipientRejectionEpisodes',
            headerName: 'recipientRejectionEpisodes',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Rejection<br />Episodes
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientRejectionEpisodeDetail',
            field: 'recipientRejectionEpisodeDetail',
            headerName: 'recipientRejectionEpisodeDetail',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Rejection<br />Episode
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine1Month',
            field: 'recipientCreatinine1Month',
            headerName: 'recipientCreatinine1Month',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />1mo
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine1MonthDate',
            field: 'recipientCreatinine1MonthDate',
            headerName: 'recipientCreatinine1MonthDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />1mo Date
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine6Month',
            field: 'recipientCreatinine6Month',
            headerName: 'recipientCreatinine6Month',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />6mo
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine6MonthDate',
            field: 'recipientCreatinine6MonthDate',
            headerName: 'recipientCreatinine6MonthDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />6mo Date
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine1Year',
            field: 'recipientCreatinine1Year',
            headerName: 'recipientCreatinine1Year',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />1yr
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine1YearDate',
            field: 'recipientCreatinine1YearDate',
            headerName: 'recipientCreatinine1YearDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />1yr Date
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine2Year',
            field: 'recipientCreatinine2Year',
            headerName: 'recipientCreatinine2Year',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />2yr
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine2YearDate',
            field: 'recipientCreatinine2YearDate',
            headerName: 'recipientCreatinine2YearDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />2yr Date
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine3Year',
            field: 'recipientCreatinine3Year',
            headerName: 'recipientCreatinine3Year',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />3yr
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine3YearDate',
            field: 'recipientCreatinine3YearDate',
            headerName: 'recipientCreatinine3YearDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />3yr Date
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine4Year',
            field: 'recipientCreatinine4Year',
            headerName: 'recipientCreatinine4Year',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />4yr
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine4YearDate',
            field: 'recipientCreatinine4YearDate',
            headerName: 'recipientCreatinine4YearDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />4yr Date
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine5Year',
            field: 'recipientCreatinine5Year',
            headerName: 'recipientCreatinine5Year',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />5yr
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine5YearDate',
            field: 'recipientCreatinine5YearDate',
            headerName: 'recipientCreatinine5YearDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />5yr Date
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine6Year',
            field: 'recipientCreatinine6Year',
            headerName: 'recipientCreatinine6Year',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />6yr
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine6YearDate',
            field: 'recipientCreatinine6YearDate',
            headerName: 'recipientCreatinine6YearDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />6yr Date
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine7Year',
            field: 'recipientCreatinine7Year',
            headerName: 'recipientCreatinine7Year',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />7yr
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine7YearDate',
            field: 'recipientCreatinine7YearDate',
            headerName: 'recipientCreatinine7YearDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />7yr Date
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine8Year',
            field: 'recipientCreatinine8Year',
            headerName: 'recipientCreatinine8Year',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />8yr
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine8YearDate',
            field: 'recipientCreatinine8YearDate',
            headerName: 'recipientCreatinine8YearDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />8yr Date
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine9Year',
            field: 'recipientCreatinine9Year',
            headerName: 'recipientCreatinine9Year',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />9yr
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine9YearDate',
            field: 'recipientCreatinine9YearDate',
            headerName: 'recipientCreatinine9YearDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />9yr Date
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine10Year',
            field: 'recipientCreatinine10Year',
            headerName: 'recipientCreatinine10Year',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />10yr
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientCreatinine10YearDate',
            field: 'recipientCreatinine10YearDate',
            headerName: 'recipientCreatinine10YearDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Creatinine<br />10yr Date
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientIsAlive',
            field: 'recipientIsAlive',
            headerName: 'recipientIsAlive',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Is<br />Alive
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientDeceasedDate',
            field: 'recipientDeceasedDate',
            headerName: 'recipientDeceasedDate',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Deceased<br />Date
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientDeceasedPrimaryCause',
            field: 'recipientDeceasedPrimaryCause',
            headerName: 'recipientDeceasedPrimaryCause',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Deceased<br />Primary<br />Cause
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'recipientDeceasedSecondaryCause',
            field: 'recipientDeceasedSecondaryCause',
            headerName: 'recipientDeceasedSecondaryCause',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Deceased<br />Secondary<br />Cause
                </SortableHeaderComponent>,
            width: 75,
        },
        {
            colId: 'kidneyLifeYears',
            field: 'kidneyLifeYears',
            headerName: 'kidneyLifeYears',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Kidney<br />Life<br />Years
                </SortableHeaderComponent>,
            width: 75,
        }
    ];

    const columnDefs = useMemo(() => reportColumns, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper id='grid-wrapper' className='ag-theme-bootstrap' style={{ marginBottom: 20 }}>
            <AgGridReact
                enableCellTextSelection={true}
                enableRangeSelection={true}
                alwaysShowHorizontalScroll={true}
                alwaysShowVerticalScroll={true}
                defaultColDef={{
                    cellClass: 'center-list-cell',
                    headerClass: 'center-list-header',
                    sortable: true,
                    resizable: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={60}
                noRowsOverlayComponentFramework={() =>
                    <div>No data for the selected date range...</div>}
                onGridReady={onGridReady}
                onGridSizeChanged={onGridSizeChanged}
                onFirstDataRendered={(props: FirstDataRenderedEvent) => {
                    // no-op: do not size-to-fit; allow horizontal scroll
                }}
                suppressCellSelection={true}
                // rowBuffer={10000}
                onSortChanged={(e: SortChangedEvent) => {
                    e.api.refreshCells();
                }}
            />
        </GridWrapper>
    );
}

export default PatientOutComesReportGrid;